import React, { useRef } from 'react'
import { VideoExtensionRequestRecord } from '@/api/videoApi'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Cancel from '@mui/icons-material/Cancel'
import { formatDuration, timeAgo, STATUS_CONFIG } from '@/utils/common/extensionRequestUtils'

interface ExtensionRequestVideoTileProps {
  req: VideoExtensionRequestRecord
  onTileClick: () => void
  onViewExtended?: () => void
  viewLoading?: boolean
  // Admin-specific props
  isAdmin?: boolean
  actionLoading?: boolean
  showRejectInput?: boolean
  rejectFeedback?: string
  onApprove?: () => void
  onReject?: () => void
  onShowReject?: (show: boolean) => void
  onFeedbackChange?: (val: string) => void
  showUserEmail?: boolean
}

const ExtensionRequestVideoTile: React.FC<ExtensionRequestVideoTileProps> = ({
  req,
  onTileClick,
  onViewExtended,
  viewLoading = false,
  isAdmin = false,
  actionLoading = false,
  showRejectInput = false,
  rejectFeedback = '',
  onApprove,
  onReject,
  onShowReject,
  onFeedbackChange,
  showUserEmail = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)

  const aspectRatio = req.aspect_ratio || '16:9'
  const isPortrait = aspectRatio === '9:16' || aspectRatio === '3:4' || aspectRatio === '9:21'
  const objectFitClass = isPortrait ? 'object-contain' : 'object-cover'
  const cfg = STATUS_CONFIG[req.status] ?? STATUS_CONFIG.pending

  const renderVideoContent = () => {
    if (req.video_url) {
      return (
        <video
          ref={videoRef}
          src={req.video_url}
          poster={req.thumbnail_url || undefined}
          muted
          loop
          playsInline
          onMouseEnter={() => videoRef.current?.play().catch(() => {})}
          onMouseLeave={() => {
            if (videoRef.current) {
              videoRef.current.pause()
              videoRef.current.currentTime = 0
            }
          }}
          className={`w-full h-full ${objectFitClass}`}
        >
          {!isAdmin && <track kind="captions" />}
        </video>
      )
    }
    if (req.thumbnail_url) {
      return <img src={req.thumbnail_url} alt="thumbnail" className="w-full h-full object-cover" />
    }
    return <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">No preview</div>
  }

  return (
    <button
      type="button"
      className="block w-full p-0 rounded-xl overflow-hidden shadow-lg hover:shadow-xl relative bg-gray-900 group cursor-pointer text-left border-0 outline-none focus-visible:ring-2 focus-visible:ring-white/60"
      style={{ aspectRatio: '16/9' }}
      onClick={onTileClick}
    >
      {renderVideoContent()}

      <div className="absolute inset-0 pointer-events-none group-hover:bg-black/30 transition-all duration-300 flex flex-col justify-between">
        {/* Top badges - always visible */}
        <div className="flex items-start justify-between gap-2 p-2.5">
          <span
            className="px-2.5 py-1 text-xs font-semibold text-white rounded-full shadow"
            style={{ background: 'linear-gradient(to right, #F69A7E, #939BEC)' }}
          >
            → {formatDuration(req.target_duration)}
          </span>
          <span className="px-2 py-1 text-xs text-white bg-black/50 rounded-full">
            {timeAgo(req.created_at)}
          </span>
        </div>

        {/* Bottom section - status always visible, details on hover */}
        <div className="relative pointer-events-auto">
          {/* Always visible status badge */}
          <div className="p-2.5 flex items-center justify-between gap-2">
            <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-white shadow-md ${cfg.color}`}>
              {cfg.icon}
              {cfg.label}
            </div>
            {req.status === 'completed' && req.extended_video_id && (
              <button
                type="button"
                disabled={viewLoading}
                onClick={e => {
                  e.stopPropagation()
                  onViewExtended?.()
                }}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-white shadow transition hover:opacity-90 disabled:cursor-not-allowed"
                style={{ 
                  background: viewLoading 
                    ? 'rgba(0, 0, 0, 0.5)' 
                    : 'linear-gradient(to right, #F69A7E, #939BEC)' 
                }}
              >
                {viewLoading ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <PlayCircleIcon style={{ fontSize: '13px' }} />
                    <span>View Extended</span>
                  </>
                )}
              </button>
            )}
          </div>

          {/* Hover-only details section */}
          <div
            className="px-2.5 pb-2.5 flex flex-col gap-2 max-h-0 opacity-0 overflow-hidden group-hover:max-h-40 group-hover:opacity-100 transition-all duration-200"
          >
            {/* User email for admin view */}
            {showUserEmail && req.user_email && (
              <span className="text-xs text-white/90 font-medium truncate drop-shadow">{req.user_email}</span>
            )}

            {/* Prompt display */}
            {req.prompt && (
              <span className="text-xs text-white/70 line-clamp-1 drop-shadow">{req.prompt}</span>
            )}

            {/* Rejection feedback for user view */}
            {!isAdmin && req.status === 'rejected' && req.feedback && (
              <span className="text-xs text-red-300 line-clamp-2 drop-shadow">"{req.feedback}"</span>
            )}

            {/* Admin-specific: Reject input field */}
            {isAdmin && req.status === 'pending' && showRejectInput && (
              <input
                type="text"
                placeholder="Reason (optional)"
                value={rejectFeedback}
                onChange={e => onFeedbackChange?.(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs border border-white/30 bg-black/50 text-white placeholder-white/50 rounded-lg focus:outline-none focus:ring-1 focus:ring-white/50"
              />
            )}

            {/* Admin-specific: Approve/Reject buttons */}
            {isAdmin && req.status === 'pending' && (
              <div className="flex items-center gap-1.5 justify-end">
                {showRejectInput ? (
                  <>
                    <button
                      onClick={e => {
                        e.stopPropagation()
                        onShowReject?.(false)
                      }}
                      className="px-2.5 py-1 text-xs text-white/80 border border-white/30 rounded-lg hover:bg-white/10 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={e => {
                        e.stopPropagation()
                        onReject?.()
                      }}
                      disabled={actionLoading}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition disabled:opacity-50"
                    >
                      <Cancel style={{ fontSize: '12px' }} />
                      Confirm
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={e => {
                        e.stopPropagation()
                        onShowReject?.(true)
                      }}
                      disabled={actionLoading}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white border border-white/40 rounded-lg hover:bg-white/10 transition disabled:opacity-50"
                    >
                      <Cancel style={{ fontSize: '12px' }} />
                      Reject
                    </button>
                    <button
                      onClick={e => {
                        e.stopPropagation()
                        onApprove?.()
                      }}
                      disabled={actionLoading}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white rounded-lg transition disabled:opacity-50 hover:opacity-90"
                      style={{ background: 'linear-gradient(to right, #F69A7E, #939BEC)' }}
                    >
                      {actionLoading ? (
                        <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <CheckCircle style={{ fontSize: '12px' }} />
                      )}
                      Approve
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}

export default ExtensionRequestVideoTile
